import { ResumeProject } from '../../types';
import { Plus, X } from 'lucide-react';
import { RichTextEditor } from '../ui/RichTextEditor';

interface ProjectsSectionProps {
    projects: ResumeProject[];
    onChange: (projects: ResumeProject[]) => void;
}

export function ProjectsSection({ projects, onChange }: ProjectsSectionProps) {
    const addProject = () => {
        onChange([
            ...projects,
            {
                id: Date.now().toString(),
                name: '',
                description: '',
                highlights: [],
            },
        ]);
    };

    const removeProject = (index: number) => {
        onChange(projects.filter((_, i) => i !== index));
    };

    const updateProject = (index: number, field: keyof ResumeProject, value: any) => {
        const newProjects = [...projects];
        newProjects[index] = { ...newProjects[index], [field]: value };
        onChange(newProjects);
    };

    const addHighlight = (projectIndex: number) => {
        const newProjects = [...projects];
        newProjects[projectIndex].highlights.push('');
        onChange(newProjects);
    };

    const removeHighlight = (projectIndex: number, highlightIndex: number) => {
        const newProjects = [...projects];
        newProjects[projectIndex].highlights = newProjects[projectIndex].highlights.filter(
            (_, i) => i !== highlightIndex
        );
        onChange(newProjects);
    };

    const updateHighlight = (projectIndex: number, highlightIndex: number, value: string) => {
        const newProjects = [...projects];
        newProjects[projectIndex].highlights[highlightIndex] = value;
        onChange(newProjects);
    };

    return (
        <div className="card">
            <div className="flex justify-between items-center mb-4">
                <h2 className="section-title mb-0">Projects</h2>
                <button onClick={addProject} className="btn btn-secondary text-sm flex items-center gap-1">
                    <Plus size={16} />
                    Add Project
                </button>
            </div>

            <div className="space-y-4">
                {projects.map((project, projectIndex) => (
                    <div key={project.id} className="border border-gray-200 rounded-lg p-4 relative">
                        <button
                            onClick={() => removeProject(projectIndex)}
                            className="absolute top-2 right-2 text-red-600 hover:text-red-700"
                        >
                            <X size={20} />
                        </button>

                        <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="label">Project Name *</label>
                                    <input
                                        type="text"
                                        value={project.name}
                                        onChange={(e) => updateProject(projectIndex, 'name', e.target.value)}
                                        className="input"
                                        placeholder="My Awesome Project"
                                    />
                                </div>
                                <div>
                                    <label className="label">URL</label>
                                    <input
                                        type="url"
                                        value={project.url || ''}
                                        onChange={(e) => updateProject(projectIndex, 'url', e.target.value)}
                                        className="input"
                                        placeholder="https://github.com/username/project"
                                    />
                                </div>
                            </div>

                            <div>
                                <RichTextEditor
                                    label="Description"
                                    value={project.description}
                                    onChange={(value) => updateProject(projectIndex, 'description', value)}
                                    placeholder="Describe the project goal, your specific role, technologies used, and outcomes..."
                                />
                            </div>

                            <div>
                                <div className="flex justify-between items-center mb-2">
                                    <label className="label mb-0">Highlights</label>
                                    <button
                                        onClick={() => addHighlight(projectIndex)}
                                        className="text-sm text-primary-600 hover:text-primary-700"
                                    >
                                        + Add Highlight
                                    </button>
                                </div>

                                {project.highlights.map((highlight, highlightIndex) => (
                                    <div key={highlightIndex} className="flex gap-2 mb-2">
                                        <textarea
                                            value={highlight}
                                            onChange={(e) => updateHighlight(projectIndex, highlightIndex, e.target.value)}
                                            className="input flex-1 min-h-[60px]"
                                            placeholder="Key achievement or feature..."
                                        />
                                        <button
                                            onClick={() => removeHighlight(projectIndex, highlightIndex)}
                                            className="text-red-600 hover:text-red-700"
                                        >
                                            <X size={20} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                ))}

                {projects.length === 0 && (
                    <p className="text-gray-500 text-center py-4">No projects added yet</p>
                )}
            </div>
        </div>
    );
}
